import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import * as fromShipments from './../+state/shipments.reducer';
import { ShipmentsActions } from '../+state/shipments.actions';
import { ShipmentsServiceBase } from '../shipments-service-base';
import * as ShipmentsSelectors from './../+state/shipments.selectors';

@Injectable({
    providedIn: 'root',
})
export class ShipmentExistsGuard implements CanActivate, CanActivateChild {
    constructor(
        private store: Store<fromShipments.ShipmentsPartialState>,
        private service: ShipmentsServiceBase,
        private router: Router
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        state: RouterStateSnapshot
    ): Observable<boolean> {
        return this.hasShipment(route.params['id'], route);
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> {
        return this.canActivate(childRoute, state);
    }

    private hasShipmentInStore(id: string): Observable<boolean> {
        return this.store.pipe(
            select(ShipmentsSelectors.getShipmentsEntities),
            map(entities => !!entities[id]),
            take(1)
        );
    }

    private hasShipmentInApi(
        id: string,
        next: ActivatedRouteSnapshot
    ): Observable<boolean> {
        return this.service.getShipment(id).pipe(
            map(shipment => ShipmentsActions.loadShipmentSuccess({ shipment })),
            tap(action => this.store.dispatch(action)),
            map(shipment => !!shipment),
            catchError(error => {
                this.router.navigate([`/error/${error.status}`], {
                    queryParams: {
                        source: next.pathFromRoot.map(p => p.url).join('/'),
                        errorName: error.statusText,
                    },
                });

                return of(false);
            })
        );
    }

    private hasShipment(
        id: string,
        next: ActivatedRouteSnapshot
    ): Observable<boolean> {
        return this.hasShipmentInStore(id).pipe(
            switchMap(() => {
                // Omit the store as we're loading a richer object
                // if (inStore) {
                //     return of(inStore);
                // }
                return this.hasShipmentInApi(id, next);
            })
        );
    }
}
